import dayjs from "dayjs";
// Fixed number of measurement rows — all start empty, user types the label
const ROW_COUNT = 9;
const ROW_INDICES = Array.from({ length: ROW_COUNT }, (_, i) => i);

export function handlePrint(printRef) {
    const content = printRef.current;
    if (!content) return;

    const printWindow = window.open("", "_blank", "width=900,height=700");
    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Lymphedema Assessment Sheet</title>
            <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }

                body {
                    font-family: Georgia, 'Times New Roman', serif;
                    font-size: 11px;
                    color: #000;
                    background: #fff;
                    padding: 20px 28px;
                }

                h2 {
                    text-align: center;
                    font-size: 15px;
                    font-weight: bold;
                    text-decoration: underline;
                    letter-spacing: 0.05em;
                    margin-bottom: 10px;
                    padding-bottom: 6px;
                    border-bottom: 2px solid #555;
                }

                /* ── Field rows ── */
                .field-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px 20px;
                    align-items: flex-end;
                    padding: 5px 0;
                    border-bottom: 1px dashed #bbb;
                    margin-bottom: 5px;
                }

                .field-group {
                    display: flex;
                    align-items: flex-end;
                    gap: 5px;
                    flex: 1;
                    min-width: 120px;
                }

                .field-label {
                    font-weight: bold;
                    white-space: nowrap;
                    font-size: 11px;
                }

                .field-value {
                    border-bottom: 1px solid #555;
                    flex: 1;
                    min-width: 60px;
                    padding: 1px 3px;
                    font-size: 11px;
                    min-height: 16px;
                }

                .field-value-multiline {
                    border-bottom: 1px solid #555;
                    flex: 1;
                    min-width: 80px;
                    padding: 2px 3px;
                    min-height: 32px;
                    font-size: 11px;
                    white-space: pre-wrap;
                }

                /* ── Checkboxes ── */
                .check-group {
                    display: flex;
                    gap: 10px;
                    align-items: center;
                }

                .check-item {
                    display: flex;
                    align-items: center;
                    gap: 3px;
                    font-size: 11px;
                }

                .check-box {
                    width: 11px;
                    height: 11px;
                    border: 1.5px solid #444;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 9px;
                    flex-shrink: 0;
                }

                .check-box.checked::after { content: "✓"; font-weight: bold; }

                /* ── Body diagram + side fields ── */
                .body-section {
                    display: flex;
                    gap: 16px;
                    padding: 6px 0;
                    border-bottom: 1px dashed #bbb;
                    margin-bottom: 5px;
                }

                .body-image {
                    width: 140px;
                    flex-shrink: 0;
                }

                .body-image img {
                    width: 100%;
                    height: auto;
                }

                .body-fields {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                /* ── Measurement table ── */
                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 10px;
                    margin-bottom: 6px;
                }

                th, td {
                    border: 1px solid #666;
                    padding: 3px 5px;
                    text-align: center;
                }

                th {
                    background: #e8e8e8;
                    font-weight: bold;
                }

                td.point-cell { text-align: left; }

                /* ── Signatures ── */
                .signatures {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 30px;
                    padding-top: 10px;
                    border-top: 1px solid #aaa;
                }

                .sig-block {
                    text-align: center;
                }

                .sig-line {
                    width: 160px;
                    border-bottom: 1px solid #555;
                    height: 30px;
                    margin-bottom: 4px;
                }

                @media print {
                    body { padding: 10px 16px; }
                    @page { margin: 10mm; size: A4; }
                }
            </style>
        </head>
        <body>
            ${content.innerHTML}
        </body>
        </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
        printWindow.print();
        printWindow.close();
    }, 400);
}

// ── Printable snapshot builder ────────────────────────────────────────────────
// Reads form values and lymphedemaData to render a static HTML snapshot
// (no Ant Design widgets — just plain HTML) so the print window looks clean.

export function buildPrintLymphedemaHTML({ f, lymphedemaData, patientData }) {
    const checked = (val) =>
        `<span class="check-box checked"></span>`;
    const unchecked = () =>
        `<span class="check-box"></span>`;
    const box = (value, target) =>
        value.includes(target) || (Array.isArray(value) && value.includes(target))
            ? checked()
            : unchecked();

    const val = (v) => (v !== undefined && v !== null && v !== "" ? String(v) : "");

    const treatOpts = f.ptTreatmentOptions || [];
    const labNotes = f.ptLabNotes || [];
    const lymphEdema = f.ptLymphaticEdema || [];
    const sessionsNum = f.sessionsNum || [];
    const gender = f.ptGender || [];

    const measurements = lymphedemaData?.measurements || [];

    const measRows = ROW_INDICES.map((idx) => {
        const m = measurements[idx] || {};
        const pointVal = f[`ptMeasPoint_${idx}`] ?? m.pointOfMeasurement ?? "";
        const cols = [1, 2, 3, 4].map((col) => {
            const rt = val(f[`ptMeas_${idx}_${col}_RT`] ?? m[`session${col}RT`] ?? "");
            const lt = val(f[`ptMeas_${idx}_${col}_LT`] ?? m[`session${col}LT`] ?? "");
            return `<td>${rt}</td><td>${lt}</td>`;
        }).join("");
        return `<tr>
            <td class="point-cell">${val(pointVal)}</td>
            ${cols}
        </tr>`;
    }).join("");

    const sessionDates = [1, 2, 3, 4].map((col) => {
        const d = f[`session${col}Date`];
        const dateStr = d && dayjs.isDayjs(d) && d.isValid()
            ? d.format("DD/MM/YYYY") : "";
        return `<th colspan="2">${dateStr}</th>`;
    }).join("");

    return `
        <h2>Lymphedema Assessment Sheet</h2>

        <!-- Name / Age / Gender / Date -->
        <div class="field-row">
            <div class="field-group" style="flex:2">
                <span class="field-label">Name:</span>
                <span class="field-value">${val(f.ptName)}</span>
            </div>
            <div class="field-group" style="flex:0 0 auto">
                <span class="field-label">Age:</span>
                <span class="field-value" style="min-width:30px">${val(f.ptAge)}</span>
            </div>
            <div class="field-group" style="flex:0 0 auto">
                <span class="field-label">Gender:</span>
                <span class="check-group">
                    <span class="check-item">${box(gender, "M")} M.</span>
                    <span class="check-item">${box(gender, "F")} F.</span>
                </span>
            </div>
            <div class="field-group" style="flex:0 0 auto">
                <span class="field-label">Date:</span>
                <span class="field-value" style="min-width:70px">${val(f.ptDate)}</span>
            </div>
        </div>

        <!-- Diagnosis / Phone -->
        <div class="field-row">
            <div class="field-group" style="flex:2">
                <span class="field-label">Diagnosis:</span>
                <span class="field-value">${val(f.ptDiagnosis)}</span>
            </div>
            <div class="field-group" style="flex:1">
                <span class="field-label">Phone Num:</span>
                <span class="field-value">${val(f.ptPhone)}</span>
            </div>
        </div>

        <!-- Past History -->
        <div class="field-row">
            <div class="field-group" style="flex:1">
                <span class="field-label">Past. History:</span>
                <span class="field-value">${val(f.ptPastHistory)}</span>
            </div>
        </div>

        <!-- Chief Complain -->
        <div class="field-row">
            <div class="field-group" style="flex:1">
                <span class="field-label">Chief Complain:</span>
                <span class="field-value">${val(f.ptChiefComplain)}</span>
            </div>
        </div>

        <!-- Job & Occupation -->
        <div class="field-row">
            <div class="field-group" style="flex:1">
                <span class="field-label">Job & Occupation:</span>
                <span class="field-value">${val(f.ptJob)}</span>
            </div>
        </div>

        <!-- Pain score / distribution / lymphatic edema -->
        <div class="field-row">
            <div class="field-group" style="flex:0 0 auto">
                <span class="field-label">Pain score:</span>
                <span class="field-value" style="min-width:28px;text-align:center">${val(f.ptPainScore)}</span>
                <span class="field-label">/10</span>
            </div>
            <div class="field-group" style="flex:1">
                <span class="field-label">Pain distribution:</span>
                <span class="field-value">${val(f.ptPainDistribution)}</span>
            </div>
            <div class="field-group" style="flex:0 0 auto">
                <span class="field-label">Lymphatic edema:</span>
                <span class="check-group">
                    <span class="check-item">${box(lymphEdema, "yes")} Yes</span>
                    <span class="check-item">${box(lymphEdema, "no")} No.</span>
                </span>
            </div>
        </div>

        <!-- Radio Investigations -->
        <div class="field-row">
            <div class="field-group" style="flex:1;align-items:flex-start;padding-top:3px">
                <span class="field-label">Radio Investigations:</span>
                <span class="field-value-multiline">${val(f.ptRadioInvestigations)}</span>
            </div>
        </div>

        <!-- Lab Notes + Results -->
        <div class="field-row">
            <div class="field-group" style="flex:0 0 auto">
                <span class="field-label">Lab Notes:</span>
                <span class="check-group">
                    <span class="check-item">${box(labNotes, "yes")} Yes</span>
                    <span class="check-item">${box(labNotes, "no")} No.</span>
                </span>
            </div>
            <div class="field-group" style="flex:1">
                <span class="field-label">Results:</span>
                <span class="field-value">${val(f.ptLabResults)}</span>
            </div>
        </div>

        <!-- Body diagram + side fields -->
        <div class="body-section">
            <div class="body-image">
                <img src="/lymphoImg.png" alt="Body diagram" />
            </div>
            <div class="body-fields">
                <div class="field-group" style="align-items:flex-start">
                    <span class="field-label">Operations:</span>
                    <span class="field-value-multiline">${val(f.ptOperations)}</span>
                </div>
                <div class="field-group" style="align-items:flex-start">
                    <span class="field-label">Present History:</span>
                    <span class="field-value-multiline">${val(f.ptPresentHistory)}</span>
                </div>
                <div class="field-group" style="align-items:flex-start">
                    <span class="field-label">Skin Abnormalities:</span>
                    <span class="field-value-multiline">${val(f.ptSkinAbnormalities)}</span>
                </div>
                <div>
                    <span class="field-label">Plan of Treatment:</span>
                    <div class="check-group" style="margin-top:4px;flex-wrap:wrap;gap:8px">
                        <span class="check-item">${box(treatOpts, "MLD")} MLD</span>
                        <span class="check-item">${box(treatOpts, "PCD")} PCD</span>
                        <span class="check-item">${box(treatOpts, "TECAR")} TECAR</span>
                        <span class="check-item">${box(treatOpts, "Bandaging")} Bandaging</span>
                        <span class="check-item">${box(treatOpts, "Exercises")} Ex's</span>
                    </div>
                    <div class="field-group" style="margin-top:4px">
                        <span class="field-label">Other:</span>
                        <span class="field-value">${val(f.ptTreatmentOther)}</span>
                    </div>
                </div>
                <div class="field-group">
                    <span class="field-label">Precautions:</span>
                    <span class="field-value">${val(f.ptPrecautions)}</span>
                </div>
            </div>
        </div>

        <!-- Sessions Num + Measurement Table -->
        <table>
            <thead>
                <tr>
                    <th colspan="10" style="text-align:left;padding:4px 8px">
                        <span style="font-weight:bold">Sessions Num:</span>
                        <span class="check-group" style="display:inline-flex;margin-left:10px;gap:14px">
                            ${["3", "6", "9", "12", "18", "24", "More"].map(n =>
        `<span class="check-item">${box(sessionsNum, n)} ${n}</span>`
    ).join("")}
                        </span>
                    </th>
                </tr>
                <tr>
                    <th>Date:</th>
                    ${sessionDates}
                </tr>
                <tr>
                    <th>Point of Measurement</th>
                    ${[1, 2, 3, 4].map(() => `<th>RT</th><th>LT</th>`).join("")}
                </tr>
            </thead>
            <tbody>
                ${measRows}
            </tbody>
        </table>

        <!-- PT Signatures -->
        <div class="signatures">
            <div class="sig-block">
                <div class="sig-line"></div>
                <span style="font-size:11px;font-weight:bold">P.T Specialist Signature</span>
            </div>
            <div class="sig-block">
                <div class="sig-line"></div>
                <span style="font-size:11px;font-weight:bold">Consultant Signature</span>
            </div>
        </div>
    `;
}